import { useEffect, useState } from 'react';
import { api } from '../client/api';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';
import type { BoOperator } from '../client/api';
import { cn } from '../utils/cn';
import { PageContainer } from '../nav/PageContainer';

// =============================================================================
// Back-office page — PR-1c-FE (NBL_REQ_801..803).
//   테넌트 생성·관리는 /bo/org의 Owner 지정 흐름으로 통합됨.
//   § 운영자: BO operator allow-list 관리
// Gated server-side by the bo_operators allow-list; in local-dev (AUTH off)
// the surface is open so it can be exercised without a session.
// =============================================================================

const inputCls =
  'rounded border border-line bg-panel-2 px-2.5 py-1.5 text-[15px] text-text outline-none transition-colors focus:border-accent-ring placeholder:text-text-faint';
const btnPrimary =
  'inline-flex h-8 items-center gap-1.5 rounded border border-accent-ring bg-accent-soft px-3 text-[14px] font-semibold text-accent transition-colors hover:border-accent hover:bg-accent hover:text-bg disabled:cursor-not-allowed disabled:opacity-50';
const btnGhost =
  'inline-flex h-7 items-center rounded border border-line px-2.5 text-[13px] text-text-dim transition-colors hover:border-line-strong hover:text-text disabled:opacity-40';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded border border-line bg-panel p-5">
      <h2 className="mb-4 text-[16px] font-semibold tracking-wide text-text">{title}</h2>
      {children}
    </section>
  );
}

function OperatorsSection() {
  const [operators, setOperators] = useState<BoOperator[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);

  async function load() {
    try {
      setOperators(await api.boListOperators());
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setAdding(true);
    setErr(null);
    try {
      await api.boAddOperator(email.trim().toLowerCase(), name.trim() || undefined);
      setEmail('');
      setName('');
      await load();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAdding(false);
    }
  }

  // 운영자별 '고객사 기기 운영 메일'(긴급·경고·복구) — 알림 센터의 같은 토글과 한 값(2026-10-06 대교). 실패하면 되돌린다.
  const [mailBusy, setMailBusy] = useState<string | null>(null);
  async function toggleMail(o: BoOperator) {
    if (mailBusy) return;
    const next = o.notifyEmailOpsFleet === false;
    setOperators((prev) => prev.map((x) => (x.id === o.id ? { ...x, notifyEmailOpsFleet: next } : x)));
    setMailBusy(o.id); setErr(null);
    try { const saved = await api.boSetOperatorMail(o.id, next); setOperators((prev) => prev.map((x) => (x.id === o.id ? saved : x))); }
    catch (e: unknown) { setOperators((prev) => prev.map((x) => (x.id === o.id ? o : x))); setErr(e instanceof Error ? e.message : String(e)); }
    finally { setMailBusy(null); }
  }

  async function handleRemove(id: string, email: string) {
    if (!confirm(`운영자 ${email}를 제거하시겠습니까?`)) return;
    setErr(null);
    try {
      await api.boRemoveOperator(id);
      setOperators((prev) => prev.filter((o) => o.id !== id));
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {err && <p className="rounded bg-warning/10 px-3 py-2 text-[14px] text-warning">{err}</p>}
      {loading ? <ContentSkeleton label="운영자 목록을 불러오는 중…" variant="split" /> : <>
      <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-[13px] font-medium text-text-dim">운영자 이메일</label>
          <input className={cn(inputCls, 'w-56')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="staff@platform.com" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[13px] font-medium text-text-dim">이름 (선택)</label>
          <input className={cn(inputCls, 'w-40')} value={name} onChange={(e) => setName(e.target.value)} placeholder="홍길동" />
        </div>
        <button type="submit" disabled={adding || !email.trim()} className={btnPrimary}>
          {adding ? '추가 중…' : '운영자 추가'}
        </button>
      </form>

      {operators.length === 0 ? (
        <p className="py-6 text-center text-[14px] text-text-faint">
          운영자가 없습니다. (로컬은 seed 스크립트로 첫 운영자를 부트스트랩합니다.)
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {operators.map((o) => (
            <li key={o.id} className="flex items-center gap-3 rounded border border-line bg-panel-2/40 px-3 py-2 text-[14px]">
              <span className="text-text">{o.email}</span>
              {o.name && <span className="text-text-dim">· {o.name}</span>}
              <label className="ml-auto flex items-center gap-2 text-[13px] text-text-dim" title="모든 고객사의 기기 운영 경보(긴급·경고·복구)를 이 운영자에게 메일로 보냅니다">
                <span>경보 메일</span>
                <button type="button" role="switch" aria-checked={o.notifyEmailOpsFleet !== false} aria-label={`${o.email} 경보 메일`}
                  disabled={mailBusy !== null} onClick={() => void toggleMail(o)}
                  className={cn('relative h-5 w-9 flex-none rounded-full transition-colors', o.notifyEmailOpsFleet !== false ? 'bg-accent' : 'bg-panel-3', mailBusy === o.id && 'opacity-60')}>
                  <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all', o.notifyEmailOpsFleet !== false ? 'left-[18px]' : 'left-0.5')} />
                </button>
              </label>
              <button type="button" onClick={() => void handleRemove(o.id, o.email)} className={cn(btnGhost, 'text-warning')}>
                제거
              </button>
            </li>
          ))}
        </ul>
      )}
      </>}
    </div>
  );
}

export function BackOffice(): JSX.Element {
  return (
    <PageContainer variant="content" fluid inSection>
      <div>
        <h1 className="text-[22px] font-semibold text-text">백오피스</h1>
        <p className="mt-1 text-[14px] text-text-dim">
          운영사 전용 — 최고 관리자 (H9) 관리. 조직 생성·정지는 [조직 관리]에서.
        </p>
      </div>
      {/* 테넌트 생성·정지/재개는 '조직 관리'(/bo/org)로 통합됨. */}
      <Section title="운영자">
        <OperatorsSection />
      </Section>
    </PageContainer>
  );
}
